import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../services/api';
import MealCard from './MealCard';
import ConfirmationModal from '../ConfirmationModal';
import './MealExplorer.css';

function MealExplorer({ userId, symptoms, onMealSelect }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance'); // 'relevance', 'prepTime', 'calories'
  const [favorites, setFavorites] = useState(new Set());
  const [meals, setMeals] = useState([]);
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showMealDetails, setShowMealDetails] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonMeals, setComparisonMeals] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [headerHeight, setHeaderHeight] = useState(120); // Default fallback
  
  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const headerWrapper = document.querySelector('.navigation-header-wrapper');
      if (headerWrapper) {
        const height = headerWrapper.offsetHeight;
        setHeaderHeight(height + 20); // Add 20px for extra padding
      }
    };
    
    calculateHeaderHeight();
    window.addEventListener('resize', calculateHeaderHeight);
    // Also check after a delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeaderHeight, 100);
    const timeoutId2 = setTimeout(calculateHeaderHeight, 500);
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, []);
  
  const filters = [
    { id: 'all', label: 'All Meals', icon: '🍽️', color: '#667eea' },
    { id: 'quick', label: 'Quick', icon: '⚡', color: '#f093fb' },
    { id: 'smoothies', label: 'Smoothies', icon: '🥤', color: '#4facfe' },
    { id: 'comfort', label: 'Comfort', icon: '🍲', color: '#fa709a' },
    { id: 'high_protein', label: 'High Protein', icon: '💪', color: '#feca57' },
    { id: 'high_iron', label: 'High Iron', icon: '🔴', color: '#ee5a6f' },
    { id: 'nausea_friendly', label: 'Nausea Friendly', icon: '💚', color: '#48c6ef' },
    { id: 'hydration', label: 'Hydration', icon: '💧', color: '#00f2fe' },
    { id: 'one_pot', label: 'One Pot', icon: '🍲', color: '#a8edea' },
    { id: 'breakfast', label: 'Breakfast', icon: '🌅', color: '#ffd93d' },
    { id: 'lunch', label: 'Lunch', icon: '☀️', color: '#ff6b6b' },
    { id: 'dinner', label: 'Dinner', icon: '🌙', color: '#4ecdc4' },
    { id: 'snack', label: 'Snacks', icon: '🍎', color: '#95e1d3' },
    { id: 'vegetarian', label: 'Vegetarian', icon: '🥗', color: '#6bcf7f' },
    { id: 'vegan', label: 'Vegan', icon: '🌱', color: '#51cf66' },
    { id: 'gluten_free', label: 'Gluten Free', icon: '🌾', color: '#ffd43b' },
    { id: 'low_carb', label: 'Low Carb', icon: '🥑', color: '#74c0fc' },
    { id: 'high_fiber', label: 'High Fiber', icon: '🌾', color: '#ff8787' },
    { id: 'calcium_rich', label: 'Calcium Rich', icon: '🥛', color: '#a5d8ff' },
    { id: 'folate_rich', label: 'Folate Rich', icon: '🥬', color: '#51cf66' },
    { id: 'omega3', label: 'Omega-3', icon: '🐟', color: '#339af0' },
    { id: 'easy_digest', label: 'Easy Digest', icon: '😌', color: '#ffc9c9' },
    { id: 'energy_boost', label: 'Energy Boost', icon: '⚡', color: '#ffd43b' },
    { id: 'sweet_craving', label: 'Sweet Cravings', icon: '🍰', color: '#ff8787' },
    { id: 'savory_craving', label: 'Savory Cravings', icon: '🧀', color: '#ffa94d' }
  ];
  
  const loadFavorites = useCallback(() => {
    if (!userId) return;
    try {
      const saved = localStorage.getItem(`mealFavorites_${userId}`);
      if (saved) {
        setFavorites(new Set(JSON.parse(saved)));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, [userId]);
  
  const saveFavorites = (newFavorites) => {
    if (!userId) return;
    try {
      localStorage.setItem(`mealFavorites_${userId}`, JSON.stringify([...newFavorites]));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  };
  
  const toggleFavorite = (mealId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(mealId)) {
      newFavorites.delete(mealId);
    } else {
      newFavorites.add(mealId);
    }
    setFavorites(newFavorites);
    saveFavorites(newFavorites);
  };
  
  const loadMeals = useCallback(async () => {
    if (!userId) {
      console.warn('⚠️ Cannot load meals - missing userId');
      return;
    }
    
    setLoading(true);
    try {
      let symptomsList = [];
      if (symptoms && symptoms.length > 0) {
        symptomsList = symptoms.map(s => typeof s === 'string' ? s : s.symptomName || s.name);
      }
      
      console.log('🔍 Loading meals for explorer:', { userId, filter: activeFilter, symptomsList });
      const response = await api.exploreMeals(userId, activeFilter, symptomsList);
      
      const mealsData = Array.isArray(response) ? response : (response?.data || response || []);
      setMeals(mealsData);
      console.log(`✅ Loaded ${mealsData.length} meals for explorer (filter: ${activeFilter})`);
    } catch (error) {
      console.error('❌ Error loading meals:', error);
      setMeals([]);
    } finally {
      setLoading(false);
    }
  }, [userId, activeFilter, symptoms]);

  const filterAndSortMeals = useCallback(() => {
    let filtered = [...meals];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(meal =>
        meal.name?.toLowerCase().includes(query) ||
        meal.description?.toLowerCase().includes(query) ||
        meal.ingredients?.some(ing => ing.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'prepTime') {
        const aTime = parseInt(a.prepTime) || 999;
        const bTime = parseInt(b.prepTime) || 999;
        return aTime - bTime;
      }
      if (sortBy === 'calories') {
        const aCal = parseInt(a.calories) || 0;
        const bCal = parseInt(b.calories) || 0;
        return aCal - bCal;
      }
      // Relevance: favorites first, then by name
      const aFav = favorites.has(a.id || a.name) ? 1 : 0;
      const bFav = favorites.has(b.id || b.name) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      return (a.name || '').localeCompare(b.name || '');
    });
    
    setFilteredMeals(filtered);
  }, [meals, searchQuery, sortBy, favorites]);

  useEffect(() => {
    if (userId) {
      loadMeals();
      loadFavorites();
    }
  }, [userId, activeFilter, symptoms, loadMeals, loadFavorites]);
  
  useEffect(() => {
    filterAndSortMeals();
  }, [filterAndSortMeals]);
  
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    message: ''
  });

  const handleMealClick = (meal) => {
    if (comparisonMode) {
      const mealId = meal.id || meal.name;
      if (comparisonMeals.some(m => (m.id || m.name) === mealId)) {
        setComparisonMeals(prev => prev.filter(m => (m.id || m.name) !== mealId));
      } else if (comparisonMeals.length < 3) {
        setComparisonMeals(prev => [...prev, meal]);
      } else {
        setFeedbackModal({
          isOpen: true,
          message: 'You can compare up to 3 meals at a time!'
        });
      }
    } else {
      setSelectedMeal(meal);
      setShowMealDetails(true);
    }
  };
  
  const handleAddToPlan = (meal, mealType) => {
    if (onMealSelect) {
      onMealSelect(mealType, meal);
      setShowMealDetails(false);
    } else {
      console.warn('⚠️ onMealSelect callback not provided');
    }
  };
  
  const clearComparison = () => {
    setComparisonMeals([]);
    setComparisonMode(false);
  };
  
  return (
    <div className="meal-explorer-modern">
      <div className="explorer-header-modern">
        <motion.div
          className="explorer-icon-wrapper"
          animate={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        >
          <div className="explorer-icon">🔍</div>
        </motion.div>
        <div className="explorer-header-text">
          <h2>Explore Meals</h2>
          <p>Discover pregnancy-friendly meals from trusted sources (Mayo Clinic, ACOG, CDC, WHO)</p>
          <div className="explorer-help">
            <span className="help-icon">💡</span>
            <span className="help-text">Use filters to find meals by type, search by name or ingredients, and click any meal to see full details. Favorite meals you love for quick access!</span>
          </div>
        </div>
      </div>
      
      <div className="explorer-controls">
        <div className="search-section">
          <div className="search-box-wrapper">
            <div className="search-box-modern">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search meals, ingredients, or nutrients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input-modern"
                title="Search by meal name, description, or ingredients. Try searching for 'iron', 'protein', or specific foods!"
              />
              {searchQuery && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <span className="search-hint">Search works across meal names, descriptions, and ingredients</span>
          </div>
          
          <div className="sort-section-wrapper">
            <div className="sort-section">
              <label className="sort-label">Sort by:</label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                title="Choose how to organize your meals"
              >
                <option value="relevance">Relevance (Favorites First)</option>
                <option value="prepTime">Prep Time (Fastest First)</option>
                <option value="calories">Calories (Lowest First)</option>
              </select>
            </div>
            <span className="sort-hint">
              {sortBy === 'relevance' && 'Your favorite meals appear first'}
              {sortBy === 'prepTime' && 'Quick meals are shown first'}
              {sortBy === 'calories' && 'Lower calorie meals are shown first'}
            </span>
          </div>
        </div>
        
        <div className="mode-toggle-section">
          <div className="mode-toggle-wrapper">
            <motion.button
              className={`mode-btn ${comparisonMode ? 'active' : ''}`}
              onClick={() => {
                setComparisonMode(!comparisonMode);
                if (comparisonMode) setComparisonMeals([]);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={comparisonMode ? 'Exit comparison mode' : 'Compare up to 3 meals side-by-side to help you decide'}
            >
              {comparisonMode ? '✅ Comparison Mode' : '⚖️ Compare Meals'}
            </motion.button>
            <span className="mode-hint">
              {comparisonMode 
                ? 'Click meals to add them to comparison (up to 3)'
                : 'Enable to compare meals side-by-side'}
            </span>
          </div>
          <div className="mode-toggle-wrapper">
            <div className="view-mode-toggle">
              <motion.button
                className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Grid view - See meals in a card grid layout"
              >
                ⊞ Grid
              </motion.button>
              <motion.button
                className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="List view - See meals in a detailed list layout"
              >
                ☰ List
              </motion.button>
            </div>
            <span className="mode-hint">
              {viewMode === 'grid' ? 'Card grid layout' : 'Detailed list layout'}
            </span>
          </div>
          {comparisonMode && comparisonMeals.length > 0 && (
            <motion.button
              className="clear-comparison-btn"
              onClick={clearComparison}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Clear all meals from comparison"
            >
              Clear ({comparisonMeals.length})
            </motion.button>
          )}
        </div>
      </div>
      
      <div className="explorer-filters-section">
        <div className="filters-header">
          <h3>Filter by Category</h3>
          <p className="filters-description">Browse meals by type, preparation style, or nutritional focus</p>
        </div>
        <div className="explorer-filters-modern">
          {filters.map((filter) => {
            const filterDescriptions = {
              'all': 'Show all available meals',
              'quick': 'Meals that take 30 minutes or less to prepare',
              'smoothies': 'Nutritious smoothies and drinks',
              'comfort': 'Comforting, easy-to-digest meals',
              'high_protein': 'Meals rich in protein for baby\'s growth',
              'high_iron': 'Iron-rich meals to prevent anemia',
              'nausea_friendly': 'Gentle meals for morning sickness',
              'hydration': 'Hydrating meals and drinks',
              'one_pot': 'Simple one-pot meals for easy cooking',
              'breakfast': 'Perfect morning meals to start your day',
              'lunch': 'Midday meals to keep you energized',
              'dinner': 'Evening meals for a satisfying end to your day',
              'snack': 'Healthy snacks between meals',
              'vegetarian': 'Plant-based meals without meat',
              'vegan': '100% plant-based meals',
              'gluten_free': 'Meals without gluten',
              'low_carb': 'Low carbohydrate options',
              'high_fiber': 'Fiber-rich meals for digestion',
              'calcium_rich': 'Calcium-packed meals for strong bones',
              'folate_rich': 'Folate-rich meals for baby development',
              'omega3': 'Omega-3 rich meals for brain development',
              'easy_digest': 'Easy to digest, gentle on stomach',
              'energy_boost': 'Meals that boost your energy',
              'sweet_craving': 'Satisfy your sweet tooth healthily',
              'savory_craving': 'Satisfy your savory cravings'
            };
            
            return (
              <motion.button
                key={filter.id}
                className={`explorer-filter-modern ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  '--filter-color': filter.color
                }}
                title={filterDescriptions[filter.id] || filter.label}
              >
                <span className="filter-icon">{filter.icon}</span>
                <span className="filter-label">{filter.label}</span>
                {filteredMeals.length > 0 && activeFilter === filter.id && (
                  <span className="filter-count">({filteredMeals.length})</span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
      
      {comparisonMode && comparisonMeals.length > 0 && (
        <motion.div
          className="comparison-bar"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="comparison-header">
            <h3>Comparing {comparisonMeals.length} meal{comparisonMeals.length !== 1 ? 's' : ''}</h3>
            <button onClick={clearComparison}>✕ Clear</button>
          </div>
          <div className="comparison-meals">
            {comparisonMeals.map((meal, idx) => (
              <div key={meal.id || meal.name || idx} className="comparison-meal-card">
                <h4>{meal.name}</h4>
                <div className="comparison-details">
                  {meal.prepTime && <span>⏱️ {meal.prepTime}</span>}
                  {meal.calories && <span>🔥 {meal.calories} cal</span>}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
      
      {loading ? (
        <div className="loading-state-modern">
          <motion.div
            className="loading-spinner-modern"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            ⏳
          </motion.div>
          <p>Loading amazing meals for you...</p>
        </div>
      ) : (
        <>
          {filteredMeals.length > 0 ? (
            <>
              <div className="meals-count-modern">
                <div className="count-display">
                  <span className="count-number">{filteredMeals.length}</span>
                  <span className="count-text">
                    {filteredMeals.length === 1 ? 'meal' : 'meals'} found
                    {searchQuery && ` for "${searchQuery}"`}
                    {activeFilter !== 'all' && ` in ${filters.find(f => f.id === activeFilter)?.label}`}
                  </span>
                </div>
                <div className="count-actions-hint">
                  <span className="hint-icon">💡</span>
                  <span className="hint-text">
                    Click any meal card to see full details, recipe, and add it to your daily plan. 
                    Use the ❤️ button to favorite meals you love!
                  </span>
                </div>
              </div>
              
              {viewMode === 'grid' ? (
                <div className="meals-grid-modern">
                  <AnimatePresence>
                    {filteredMeals.map((meal, index) => {
                      const mealId = meal.id || meal.name || `meal-${index}`;
                      const isFavorite = favorites.has(mealId);
                      const isInComparison = comparisonMeals.some(m => (m.id || m.name) === mealId);
                      
                      return (
                        <motion.div
                          key={mealId}
                          className={`meal-card-wrapper ${isInComparison ? 'in-comparison' : ''}`}
                          initial={{ opacity: 0, scale: 0.9, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          whileHover={{ scale: 1.03, y: -4 }}
                        >
                          <div className="meal-card-actions">
                            <motion.button
                              className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(mealId);
                              }}
                              whileHover={{ scale: 1.2 }}
                              whileTap={{ scale: 0.9 }}
                              title={isFavorite ? 'Remove from favorites' : 'Add to favorites for quick access'}
                            >
                              {isFavorite ? '❤️' : '🤍'}
                            </motion.button>
                            {comparisonMode && (
                              <motion.button
                                className={`compare-btn ${isInComparison ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMealClick(meal);
                                }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title={isInComparison ? 'Remove from comparison' : 'Add to comparison (up to 3 meals)'}
                              >
                                {isInComparison ? '✓' : '⚖️'}
                              </motion.button>
                            )}
                          </div>
                          
                          <div onClick={() => handleMealClick(meal)} style={{ cursor: 'pointer' }}>
                            <MealCard 
                              meal={meal} 
                              compact={false}
                              onRemove={null}
                              onSwap={null}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="meals-list-modern">
                  <AnimatePresence>
                    {filteredMeals.map((meal, index) => {
                      const mealId = meal.id || meal.name || `meal-${index}`;
                      const isFavorite = favorites.has(mealId);
                      const isInComparison = comparisonMeals.some(m => (m.id || m.name) === mealId);
                      
                      return (
                        <motion.div
                          key={mealId}
                          className={`meal-list-item ${isInComparison ? 'in-comparison' : ''}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3, delay: index * 0.03 }}
                          whileHover={{ x: 4, backgroundColor: 'rgba(255, 250, 252, 0.8)' }}
                        >
                          <div className="meal-list-content" onClick={() => handleMealClick(meal)}>
                            <div className="meal-list-main">
                              <div className="meal-list-header">
                                <h4 className="meal-list-name">{meal.name}</h4>
                                <div className="meal-list-badges">
                                  {meal.prepTimeMinutes && (
                                    <span className="meal-list-badge">
                                      ⏱️ {meal.prepTimeMinutes} min
                                    </span>
                                  )}
                                  {meal.calories && (
                                    <span className="meal-list-badge">
                                      🔥 {meal.calories} cal
                                    </span>
                                  )}
                                  {isFavorite && (
                                    <span className="meal-list-badge favorite-badge">❤️</span>
                                  )}
                                </div>
                              </div>
                              {meal.description && (
                                <p className="meal-list-description">{meal.description}</p>
                              )}
                              {meal.nutrients && meal.nutrients.length > 0 && (
                                <div className="meal-list-nutrients">
                                  {meal.nutrients.slice(0, 5).map((nutrient, idx) => (
                                    <span key={idx} className="meal-list-nutrient-tag">
                                      {nutrient}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="meal-list-actions">
                              <motion.button
                                className={`meal-list-favorite-btn ${isFavorite ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(mealId);
                                }}
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                {isFavorite ? '❤️' : '🤍'}
                              </motion.button>
                              {comparisonMode && (
                                <motion.button
                                  className={`meal-list-compare-btn ${isInComparison ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMealClick(meal);
                                  }}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  title={isInComparison ? 'Remove from comparison' : 'Add to comparison'}
                                >
                                  {isInComparison ? '✓' : '⚖️'}
                                </motion.button>
                              )}
                              <motion.button
                                className="meal-list-view-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMealClick(meal);
                                }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="View full details"
                              >
                                👁️
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </>
          ) : (
            <motion.div
              className="empty-state-modern"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
            <div className="empty-icon-large">🍽️</div>
            <h3>No meals found</h3>
            <p className="empty-message-main">
              {searchQuery 
                ? `No meals match "${searchQuery}". Try a different search term.`
                : `No meals found for "${filters.find(f => f.id === activeFilter)?.label}". Try a different filter.`}
            </p>
            <div className="empty-suggestions">
              <h4>💡 Suggestions:</h4>
              <ul className="suggestions-list">
                {searchQuery ? (
                  <>
                    <li>Try a broader search term (e.g., "protein" instead of "chicken breast")</li>
                    <li>Search by ingredient name (e.g., "spinach", "lentils")</li>
                    <li>Search by nutrient (e.g., "iron", "calcium", "folate")</li>
                    <li>Clear your search to see all available meals</li>
                  </>
                ) : (
                  <>
                    <li>Try selecting "All Meals" to see everything available</li>
                    <li>Check other filter categories - there might be meals in different categories</li>
                    <li>Your preferences or symptoms might be filtering out meals - try adjusting your profile</li>
                  </>
                )}
              </ul>
            </div>
            {searchQuery && (
              <motion.button
                className="clear-search-action"
                onClick={() => setSearchQuery('')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear Search & Show All
              </motion.button>
            )}
            </motion.div>
          )}
        </>
      )}
      
      {/* Meal Details Modal */}
      {showMealDetails && selectedMeal && createPortal(
        <AnimatePresence mode="wait">
          <motion.div
            className="meal-details-overlay-modern"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMealDetails(false)}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999, // Very high z-index
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '20px',
              paddingTop: `${headerHeight}px`, // Dynamic padding based on actual header height
              overflowY: 'auto'
            }}
          >
            <motion.div
              className="meal-details-modal-modern"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ 
                maxHeight: `calc(100vh - ${headerHeight + 40}px)`, // Account for header and padding
                marginTop: '20px' // Small top margin for spacing
              }}
            >
              <div className="meal-details-header-modern">
                <h3>{selectedMeal.name}</h3>
                <div className="header-actions">
                  <motion.button
                    className={`favorite-btn-modal ${favorites.has(selectedMeal.id || selectedMeal.name) ? 'active' : ''}`}
                    onClick={() => toggleFavorite(selectedMeal.id || selectedMeal.name)}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {favorites.has(selectedMeal.id || selectedMeal.name) ? '❤️' : '🤍'}
                  </motion.button>
                  <button 
                    className="close-modal-btn-modern"
                    onClick={() => setShowMealDetails(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="meal-details-content-modern">
                {selectedMeal.description && (
                  <div className="meal-description-modern">
                    <p>{selectedMeal.description}</p>
                  </div>
                )}
                
                <div className="meal-meta-grid">
                  {selectedMeal.prepTime && (
                    <div className="meta-item">
                      <span className="meta-icon">⏱️</span>
                      <div>
                        <span className="meta-label">Prep Time</span>
                        <span className="meta-value">{selectedMeal.prepTime}</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.calories && (
                    <div className="meta-item">
                      <span className="meta-icon">🔥</span>
                      <div>
                        <span className="meta-label">Calories</span>
                        <span className="meta-value">{selectedMeal.calories} cal</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.difficulty && (
                    <div className="meta-item">
                      <span className="meta-icon">📊</span>
                      <div>
                        <span className="meta-label">Difficulty</span>
                        <span className="meta-value">{selectedMeal.difficulty}</span>
                      </div>
                    </div>
                  )}
                  {selectedMeal.source && (
                    <div className="meta-item">
                      <span className="meta-icon">📚</span>
                      <div>
                        <span className="meta-label">Source</span>
                        <span className="meta-value">{selectedMeal.source}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {selectedMeal.ingredients && selectedMeal.ingredients.length > 0 && (
                  <div className="meal-section-modern">
                    <h4>📝 Ingredients</h4>
                    <ul className="ingredients-list-modern">
                      {selectedMeal.ingredients.map((ingredient, idx) => (
                        <li key={idx}>{ingredient}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedMeal.recipe && (
                  <div className="meal-section-modern">
                    <h4>👨‍🍳 How to Prepare</h4>
                    <div className="recipe-steps">
                      {selectedMeal.recipe.split('\n').map((step, idx) => (
                        <div key={idx} className="recipe-step-modern">
                          <span className="step-number">{idx + 1}</span>
                          <span className="step-text">{step.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedMeal.nutrients && selectedMeal.nutrients.length > 0 && (
                  <div className="meal-section-modern">
                    <h4>💊 Key Nutrients</h4>
                    <div className="nutrients-grid-modern">
                      {selectedMeal.nutrients.map((nutrient, idx) => (
                        <span key={idx} className="nutrient-badge-modern">{nutrient}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {onMealSelect && (
                <div className="meal-details-actions-modern">
                  <p className="add-to-plan-label-modern">Add to Daily Plan:</p>
                  <div className="meal-type-buttons-modern">
                    {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => (
                      <motion.button
                        key={mealType}
                        className="add-to-plan-btn-modern"
                        onClick={() => handleAddToPlan(selectedMeal, mealType)}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {mealType === 'breakfast' && '🌅'}
                        {mealType === 'lunch' && '☀️'}
                        {mealType === 'dinner' && '🌙'}
                        {mealType === 'snack' && '🍎'}
                        {' '}
                        {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      <ConfirmationModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, message: '' })}
        onConfirm={() => {}}
        title="Comparison Limit"
        message={feedbackModal.message}
        type="info"
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default MealExplorer;
